import { NOME_LIGA, NOME_SERIE_B } from './extras';
import { nomeDaLiga } from './ligas-mundo';
import { PREMIO_CAMPEAO_COPA } from './financas';
import type { EstadoJogo } from './jogo';
import type { Calendario, Confronto } from './liga';
import { ehRodadaDeLibertadores, nomeContinentalDaRodada, textoRodadaLibertadores } from './libertadores';
import type { Rng } from './rng';
import type { Clube, Jogador } from './tipos';

/**
 * Copa do Brasil (mata-mata): oitavas, quartas e semifinais em ida e volta; final em jogo único.
 * O vencedor sai do placar agregado e, se empatar, dos pênaltis. O sorteio das oitavas já fixa o chaveamento inteiro (ver
 * `Copa.fixa`); só copas de saves antigos sorteiam cada fase quando a anterior termina. O calendário do EstadoJogo cresce a cada
 * fase concluída.
 */

export const COPA_NOME = 'Copa do Brasil';

export type NomeFase = 'Playoffs' | 'Oitavas de final' | 'Quartas de final' | 'Semifinais' | 'Final';

export interface ConfrontoCopa {
  /** mandante da ida (na final, o mandante do jogo único) */
  a: number;
  b: number;
  idaGols?: [number, number];
  voltaGols?: [number, number];
  vencedor?: number;
  /** pênaltis (a, b) quando o agregado empata */
  penaltis?: [number, number];
  /** cada cobrança da disputa, na ordem em que aconteceram */
  cobrancas?: CobrancaPenalti[];
}

export interface FaseCopa {
  nome: NomeFase;
  confrontos: ConfrontoCopa[];
  /** índices no calendário: [ida, volta] ou [jogo único] */
  rodadas: number[];
  concluida: boolean;
}

export interface Copa {
  nome: string;
  participantes: number[];
  fases: FaseCopa[];
  campeaoId: number | null;
  viceId: number | null;
  /**
   * Copa disputada junto com o campeonato de pontos corridos no mesmo save: índices do calendário reservados para as 7 rodadas da
   * copa (as rodadas "de meio de semana" entre as da liga). Sem isto, a copa é o próprio calendário (formato 'copa').
   */
  slots?: number[];
  /**
   * Chaveamento fixo: o sorteio das oitavas já define o caminho todo. O vencedor do confronto 1 de uma fase enfrenta o do 2,
   * o do 3 o do 4, e assim por diante; a metade esquerda (confrontos 1 a 4 das oitavas) só encontra a direita na final.
   * Copas de saves antigos não têm a marca e seguem sorteando cada fase quando a anterior termina.
   */
  fixa?: boolean;
}

/** Depois de quantas rodadas da liga cada rodada da copa entra no calendário misto (ida e volta com uma rodada de liga no meio). */
const CORTES_COPA = [4, 6, 10, 12, 16, 18, 24];

const PREMIO_AVANCAR: Record<NomeFase, number> = {
  Playoffs: 0,
  'Oitavas de final': 1_000_000,
  'Quartas de final': 1_500_000,
  Semifinais: 2_500_000,
  Final: 6_000_000,
};
export const PREMIO_VICE = 3_000_000;
export const RODADAS_COPA = 7; // 2 + 2 + 2 + 1
export const PATROCINIO_COPA = 2_000_000;
export const MESES_DE_FOLHA_COPA = 2;

const SEQUENCIA: NomeFase[] = ['Oitavas de final', 'Quartas de final', 'Semifinais', 'Final'];

/** Clubes que disputam a copa: com Série A e B no mesmo jogo, os da Série A (os técnicos humanos da B entram em `incluirClubesNaCopa`). */
function clubesDaCopa(e: EstadoJogo): Clube[] {
  return e.acesso ? e.clubes.filter((c) => c.divisao === 1) : e.clubes;
}

/**
 * Sorteio das oitavas, refeito a cada jogo novo e a cada temporada: 16 dos clubes disputam a copa (sorteados, com chance igual) e os
 * confrontos e o mando da ida também saem do sorteio. A posição de cada confronto no chaveamento (que é fixo) é sorteada junto.
 * Clubes de técnico humano nunca ficam de fora (ver `incluirClubesNaCopa`).
 */
export function sortearOitavas(clubes: Clube[], rng: Rng): [number, number][] {
  const ids = rng.embaralhar(clubes.map((c) => c.id)).slice(0, 16);
  const pares: [number, number][] = [];
  for (let i = 0; i + 1 < ids.length; i += 2) pares.push([ids[i] as number, ids[i + 1] as number]);
  rng.embaralhar(pares);
  // mando da ida: sorteado
  return pares.map(([a, b]) => (rng.chance(0.5) ? [a, b] : [b, a]));
}

function adicionarFase(e: EstadoJogo, nome: NomeFase, pares: [number, number][]): void {
  const copa = e.copa as Copa;
  const confrontos: ConfrontoCopa[] = pares.map(([a, b]) => ({ a, b }));
  const ida: Confronto[] = confrontos.map((c) => ({ mandante: c.a, visitante: c.b }));
  const volta: Confronto[] = confrontos.map((c) => ({ mandante: c.b, visitante: c.a }));
  const rodadas: number[] = [];
  if (copa.slots) {
    // copa junto com a liga: ocupa as próximas rodadas reservadas no calendário
    const usados = copa.fases.reduce((s, f) => s + f.rodadas.length, 0);
    const quantas = nome === 'Final' ? 1 : 2;
    for (let i = 0; i < quantas; i++) {
      const slot = copa.slots[usados + i] as number;
      e.calendario[slot] = i === 0 ? ida : volta;
      rodadas.push(slot);
    }
  } else {
    e.calendario.push(ida);
    rodadas.push(e.calendario.length - 1);
    if (nome !== 'Final') {
      e.calendario.push(volta);
      rodadas.push(e.calendario.length - 1);
    }
  }
  copa.fases.push({ nome, confrontos, rodadas, concluida: false });
}

/**
 * Coloca uma Copa do Brasil dentro do calendário da liga já existente: as 7 rodadas da copa entram entre as da liga, no mesmo save.
 * `calendarioLiga` são as rodadas só da liga (o calendário do estado é substituído pelo misto). Os resultados da copa ficam em
 * `e.resultados` junto com os da liga; use `ehRodadaDeCopa` para separá-los.
 */
export function iniciarCopaJunta(e: EstadoJogo, rng: Rng, calendarioLiga: Calendario, cortesExtras: number[] = []): number[] {
  const pares = sortearOitavas(clubesDaCopa(e), rng);
  const calendario: Calendario = [];
  const slots: number[] = [];
  const slotsExtras: number[] = [];
  // as rodadas extras (Libertadores) também ficam entre as da liga; a copa vem antes quando cai no mesmo ponto
  const cortes = [...CORTES_COPA.map((c) => ({ c, extra: false })), ...cortesExtras.map((c) => ({ c, extra: true }))].sort((a, b) => a.c - b.c);
  let corte = 0;
  for (let i = 0; i <= calendarioLiga.length; i++) {
    while (corte < cortes.length && Math.min((cortes[corte] as { c: number }).c, calendarioLiga.length) === i) {
      (cortes[corte]?.extra ? slotsExtras : slots).push(calendario.length);
      calendario.push([]);
      corte++;
    }
    if (i < calendarioLiga.length) calendario.push(calendarioLiga[i] as Calendario[number]);
  }
  e.formato = 'liga';
  e.calendario = calendario;
  e.copa = { nome: COPA_NOME, participantes: pares.flat(), fases: [], campeaoId: null, viceId: null, slots, fixa: true };
  adicionarFase(e, 'Oitavas de final', pares);
  // o técnico humano sempre disputa a copa do próprio save
  incluirClubesNaCopa(e, e.clubes.filter((c) => c.humano).map((c) => c.id));
  return slotsExtras;
}

/** A rodada (índice do calendário) é da Copa do Brasil? Em jogo só de copa, todas são; no calendário misto, as reservadas. */
export function ehRodadaDeCopa(e: EstadoJogo, rodada: number): boolean {
  return e.formato === 'copa' || Boolean(e.copa?.slots?.includes(rodada));
}

/** Há liga e copa no mesmo calendário. */
export function temCopaJunta(e: EstadoJogo): boolean {
  return e.formato !== 'copa' && Boolean(e.copa?.slots);
}

/** Quantas rodadas de liga (sem as da copa) a temporada tem. */
export function rodadasDeLiga(e: EstadoJogo): number {
  return e.formato === 'copa' ? 0 : e.calendario.length - (e.copa?.slots?.length ?? 0) - (e.libertadores?.slots.length ?? 0) - (e.sulamericana?.slots.length ?? 0);
}

/** Nº (1-based) da rodada de liga correspondente a um índice do calendário misto. */
export function numeroRodadaLiga(e: EstadoJogo, rodada: number): number {
  const slots = [...(e.copa?.slots ?? []), ...(e.libertadores?.slots ?? []), ...(e.sulamericana?.slots ?? [])];
  return rodada + 1 - slots.filter((s) => s < rodada).length;
}

/**
 * Garante que os clubes dos técnicos humanos estejam na Copa: quem ficou fora dos 16 entra na vaga do clube da CPU mais fraco
 * da primeira fase (nas oitavas e no calendário). Chame antes da primeira rodada.
 */
export function incluirClubesNaCopa(e: EstadoJogo, todos: number[]): void {
  // clube de liga estrangeira não disputa a Copa do Brasil
  const ids = todos.filter((id) => !e.clubes.find((c) => c.id === id)?.liga);
  const copa = e.copa;
  const fase = copa?.fases[0];
  // enquanto o 1º jogo da copa não foi disputado ainda dá para trocar (técnico que entra na sala antes da primeira rodada de copa)
  if (!copa || !fase || e.rodada > (fase.rodadas[0] ?? 0)) return;
  const manter = new Set(ids);
  for (const id of ids) {
    if (copa.participantes.includes(id)) continue;
    const saindo = copa.participantes
      .filter((p) => !manter.has(p))
      .map((p) => e.clubes.find((c) => c.id === p) as Clube)
      .filter((c) => !c.humano)
      .sort((a, b) => a.nivel - b.nivel)[0];
    if (!saindo) throw new Error('não há vaga na copa para esse clube');
    const trocar = (x: number): number => (x === saindo.id ? id : x);
    copa.participantes = copa.participantes.map(trocar);
    for (const c of fase.confrontos) {
      c.a = trocar(c.a);
      c.b = trocar(c.b);
    }
    for (const rodada of fase.rodadas) {
      e.calendario[rodada] = (e.calendario[rodada] ?? []).map((cf) => ({ mandante: trocar(cf.mandante), visitante: trocar(cf.visitante) }));
    }
  }
}

/** Começa uma Copa do Brasil nova: sorteia as oitavas e zera calendário e resultados. */
export function iniciarCopa(e: EstadoJogo, rng: Rng): void {
  const pares = sortearOitavas(clubesDaCopa(e), rng);
  e.formato = 'copa';
  e.calendario = [];
  e.resultados = [];
  e.rodada = 0;
  e.ultimaRodada = null;
  e.copa = { nome: COPA_NOME, participantes: pares.flat(), fases: [], campeaoId: null, viceId: null, fixa: true };
  adicionarFase(e, 'Oitavas de final', pares);
}

/** Uma cobrança da disputa: `lado` 0 = clube `a` do confronto, 1 = clube `b`. */
export interface CobrancaPenalti {
  lado: 0 | 1;
  jogador: string;
  convertido: boolean;
}

function chanceDeConverter(batedor: Jogador, goleiro: Jogador | undefined): number {
  const p = 0.72 + (batedor.overall - 45) * 0.006 - ((goleiro?.overall ?? 45) - 45) * 0.004;
  return Math.min(0.92, Math.max(0.5, p));
}

/**
 * Disputa cobrança por cobrança: 5 para cada lado alternando (para antes se um lado já não é alcançável) e depois
 * cobranças alternadas até alguém levar vantagem. `batedores` vêm na ordem de cobrança (quem terminou o jogo em campo);
 * se faltar gente, a lista recomeça.
 */
export function disputarPenaltisDetalhado(
  a: Clube,
  b: Clube,
  rng: Rng,
  batedoresA: Jogador[],
  batedoresB: Jogador[],
): { gols: [number, number]; cobrancas: CobrancaPenalti[] } {
  const lista: [Jogador[], Jogador[]] = [batedoresA, batedoresB];
  const goleiro = (c: Clube, campo: Jogador[]): Jogador | undefined => campo.find((j) => j.posicao === 'G') ?? c.elenco.find((j) => j.posicao === 'G');
  const gk: [Jogador | undefined, Jogador | undefined] = [goleiro(a, batedoresA), goleiro(b, batedoresB)];
  const gols: [number, number] = [0, 0];
  const feitas: [number, number] = [0, 0];
  const cobrancas: CobrancaPenalti[] = [];
  const cobrar = (lado: 0 | 1): void => {
    const quem = lista[lado];
    const j = quem.length ? (quem[feitas[lado] % quem.length] as Jogador) : (lado === 0 ? a : b).elenco[0] as Jogador;
    const ok = rng.chance(chanceDeConverter(j, gk[lado === 0 ? 1 : 0]));
    feitas[lado]++;
    if (ok) gols[lado]++;
    cobrancas.push({ lado, jogador: j.nome, convertido: ok });
  };
  for (let i = 0; i < 5; i++) {
    for (const lado of [0, 1] as const) {
      cobrar(lado);
      const restaA = 5 - feitas[0];
      const restaB = 5 - feitas[1];
      if (gols[0] > gols[1] + restaB || gols[1] > gols[0] + restaA) return { gols, cobrancas };
    }
  }
  while (gols[0] === gols[1]) {
    cobrar(0);
    cobrar(1);
  }
  return { gols, cobrancas };
}

/** Ordem de cobrança padrão: o batedor de pênaltis designado primeiro, depois os de maior força; o goleiro fica por último. */
export function ordemDeBatedores(clube: Clube, emCampoIds: number[]): Jogador[] {
  const campo = emCampoIds.map((id) => clube.elenco.find((j) => j.id === id)).filter((j): j is Jogador => Boolean(j));
  const linha = campo.filter((j) => j.posicao !== 'G').sort((x, y) => y.overall - x.overall);
  const goleiros = campo.filter((j) => j.posicao === 'G');
  const designado = clube.tatica.batedorPenaltiId ? linha.find((j) => j.id === clube.tatica.batedorPenaltiId) : undefined;
  return [...(designado ? [designado] : []), ...linha.filter((j) => j !== designado), ...goleiros];
}

export function disputarPenaltis(a: Clube, b: Clube, rng: Rng): [number, number] {
  // quem tem nível maior converte um pouco mais; 5 cobranças cada e depois alternadas
  const pa = Math.min(0.9, Math.max(0.6, 0.75 + (a.nivel - b.nivel) * 0.008));
  const pb = Math.min(0.9, Math.max(0.6, 0.75 + (b.nivel - a.nivel) * 0.008));
  let ga = 0;
  let gb = 0;
  for (let i = 0; i < 5; i++) {
    if (rng.chance(pa)) ga++;
    if (rng.chance(pb)) gb++;
  }
  while (ga === gb) {
    if (rng.chance(pa)) ga++;
    if (rng.chance(pb)) gb++;
  }
  return [ga, gb];
}

function golsDoJogo(e: EstadoJogo, rodada: number, mandante: number, visitante: number): [number, number] | undefined {
  return e.resultados.find((r) => r.rodada === rodada && r.mandante === mandante && r.visitante === visitante)?.gols;
}

/** Chame depois de avançar `e.rodada`: se a fase atual terminou, apura os vencedores, paga os prêmios e sorteia a próxima fase. */
export function avancarCopa(e: EstadoJogo, rng: Rng): void {
  const copa = e.copa;
  if (!copa) return;
  const fase = copa.fases.find((f) => !f.concluida);
  if (!fase) return;
  const ultima = fase.rodadas[fase.rodadas.length - 1] as number;
  if (e.rodada <= ultima) return;

  const clube = (id: number): Clube => e.clubes.find((c) => c.id === id) as Clube;
  const vencedores: number[] = [];
  for (const c of fase.confrontos) {
    c.idaGols = golsDoJogo(e, fase.rodadas[0] as number, c.a, c.b);
    if (fase.rodadas.length > 1) c.voltaGols = golsDoJogo(e, fase.rodadas[1] as number, c.b, c.a);
    const gA = (c.idaGols?.[0] ?? 0) + (c.voltaGols?.[1] ?? 0);
    const gB = (c.idaGols?.[1] ?? 0) + (c.voltaGols?.[0] ?? 0);
    if (gA !== gB) c.vencedor = gA > gB ? c.a : c.b;
    else {
      // quem terminou o jogo decisivo em campo cobra, na ordem de ordemDeBatedores
      const decisivo = e.ultimaRodada?.partidas.find((p) => (p.mandante === c.a && p.visitante === c.b) || (p.mandante === c.b && p.visitante === c.a));
      const campoDe = (id: number): number[] => {
        if (!decisivo) return [];
        return decisivo.resultado.emCampoFinal[decisivo.mandante === id ? 0 : 1];
      };
      const disputa = disputarPenaltisDetalhado(clube(c.a), clube(c.b), rng, ordemDeBatedores(clube(c.a), campoDe(c.a)), ordemDeBatedores(clube(c.b), campoDe(c.b)));
      c.penaltis = disputa.gols;
      c.cobrancas = disputa.cobrancas;
      c.vencedor = c.penaltis[0] > c.penaltis[1] ? c.a : c.b;
    }
    vencedores.push(c.vencedor);
    const premio = PREMIO_AVANCAR[fase.nome];
    const v = clube(c.vencedor);
    v.caixa += premio;
    v.balanco.premios += premio;
    if (fase.nome === 'Final') {
      const perdedor = clube(c.vencedor === c.a ? c.b : c.a);
      perdedor.caixa += PREMIO_VICE;
      perdedor.balanco.premios += PREMIO_VICE;
      copa.campeaoId = c.vencedor;
      copa.viceId = perdedor.id;
      v.caixa += PREMIO_CAMPEAO_COPA;
      v.balanco.premios += PREMIO_CAMPEAO_COPA;
    }
  }
  fase.concluida = true;
  if (fase.nome === 'Final') return;

  const proxima = SEQUENCIA[SEQUENCIA.indexOf(fase.nome) + 1] as NomeFase;
  const ordem = copa.fixa ? vencedores : rng.embaralhar([...vencedores]);
  const pares: [number, number][] = [];
  for (let i = 0; i + 1 < ordem.length; i += 2) pares.push([ordem[i] as number, ordem[i + 1] as number]);
  adicionarFase(e, proxima, pares);
}

export interface PosicaoFase {
  fase: NomeFase | null;
  perna: 'ida' | 'volta' | 'jogo único';
}

/** Fase e perna de uma rodada do calendário (null se a rodada ainda não existe). */
export function faseDaRodada(e: EstadoJogo, rodada: number): PosicaoFase | null {
  for (const f of e.copa?.fases ?? []) {
    const i = f.rodadas.indexOf(rodada);
    if (i >= 0) return { fase: f.nome, perna: f.rodadas.length === 1 ? 'jogo único' : i === 0 ? 'ida' : 'volta' };
  }
  return null;
}

/** Nome da competição em andamento. */
export function nomeCompeticao(e: EstadoJogo): string {
  if (e.formato === 'copa') return COPA_NOME;
  if (e.nomeLiga) return e.nomeLiga;
  // jogo com o mundo inteiro e técnico num clube estrangeiro: a liga dele
  const ligaHumano = e.mundo ? e.clubes.find((c) => c.id === e.humanoId)?.liga : undefined;
  if (ligaHumano) return nomeDaLiga(ligaHumano);
  return e.divisao === 'b' ? NOME_SERIE_B : NOME_LIGA;
}


/** Competição de uma rodada específica (no calendário misto, a copa tem as suas). */
export function nomeCompeticaoDaRodada(e: EstadoJogo, rodada: number): string {
  if (ehRodadaDeLibertadores(e, rodada)) return nomeContinentalDaRodada(e, rodada);
  return ehRodadaDeCopa(e, rodada) ? COPA_NOME : nomeCompeticao(e);
}

export function textoRodada(e: EstadoJogo, rodada: number): string {
  if (ehRodadaDeLibertadores(e, rodada)) return textoRodadaLibertadores(e, rodada);
  if (!ehRodadaDeCopa(e, rodada)) return `Liga - ${numeroRodadaLiga(e, rodada)}ª rodada`;
  const p = faseDaRodada(e, rodada);
  return p?.fase ? `${COPA_NOME} - ${p.fase}${p.perna === 'jogo único' ? '' : ` (${p.perna})`}` : COPA_NOME;
}

/** Até onde o clube foi na copa: posição equivalente (1 campeão, 2 vice, 3 semifinal, 5 quartas, 9 oitavas, 17 fora) e texto. */
export function campanhaNaCopa(e: EstadoJogo, clubeId: number): { posicao: number; texto: string } {
  const copa = e.copa;
  if (!copa || !copa.participantes.includes(clubeId)) return { posicao: 17, texto: 'Não participou' };
  if (copa.campeaoId === clubeId) return { posicao: 1, texto: 'Campeão' };
  if (copa.viceId === clubeId) return { posicao: 2, texto: 'Vice-campeão' };
  let ultima: FaseCopa | undefined;
  for (const f of copa.fases) if (f.confrontos.some((c) => c.a === clubeId || c.b === clubeId)) ultima = f;
  // fase em andamento: o clube ainda está na disputa (só é "eliminado" depois que a fase termina sem ele avançar)
  if (ultima && !ultima.concluida) return { posicao: 9, texto: `Disputando ${ultima.nome.toLowerCase()}` };
  switch (ultima?.nome) {
    case 'Semifinais':
      return { posicao: 3, texto: 'Eliminado nas semifinais' };
    case 'Quartas de final':
      return { posicao: 5, texto: 'Eliminado nas quartas' };
    case 'Oitavas de final':
      return { posicao: 9, texto: 'Eliminado nas oitavas' };
    default:
      return { posicao: 2, texto: 'Final' };
  }
}

/** Os clubes que jogam a rodada atual (para o online: só esses precisam confirmar). */
export function clubesDaRodada(e: EstadoJogo): number[] {
  return (e.calendario[e.rodada] ?? []).flatMap((c) => [c.mandante, c.visitante]);
}
